import React, { useState } from "react";

import { Button } from "../../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../components/ui/command";
import { SelectorTriggerLabel } from "../../../../components/ui/selectors/selector-trigger";
import { useMenuNavMode } from "../../../../components/ui/selectors/use-menu-nav-mode";
import { useTriggerWidth } from "../../../../components/ui/use-trigger-width";
import type { MediaCollection } from "../../../../api/media-library";
import { cn } from "../../../../lib/utils";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "../../../../components/ui/dropdown-trigger-styles";
import { CollectionColor } from "../collection-color";
import {
  CollectionMenuTick,
  collectionMenuClassName,
  collectionMenuRowClassName,
  filterCollectionsByName,
} from "./collection-selector";

interface Props {
  collections: Array<MediaCollection>;
  selectedIds: string[];
  className?: string;
  disabled?: boolean;
  onChange(value: string[]): void;
}

export function CollectionSelectorMulti(props: Props) {
  const [open, setOpen] = useState(false);
  const { navMode, navHandlers } = useMenuNavMode(open);
  const { triggerRef, triggerWidthStyle } = useTriggerWidth<HTMLButtonElement>(
    open,
    240
  );

  const handleToggle = (collectionId: string) => {
    const isSelected = props.selectedIds.includes(collectionId);
    if (isSelected) {
      props.onChange(props.selectedIds.filter((id) => id !== collectionId));
    } else {
      props.onChange([...props.selectedIds, collectionId]);
    }
  };

  const handleRemove = (collectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    props.onChange(props.selectedIds.filter((id) => id !== collectionId));
  };

  const selectedCollections = props.collections.filter((c) =>
    props.selectedIds.includes(c._id)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full min-h-[38px] h-auto justify-between rounded-lg font-inter text-sm font-medium",
            dropdownTriggerClassName,
            props.className
          )}
          disabled={props.disabled}
        >
          <div className="flex min-w-0 flex-1 flex-wrap gap-1">
            {selectedCollections.length > 0 ? (
              selectedCollections.map((collection) => (
                <span
                  key={collection._id}
                  className="inline-flex min-w-0 items-center gap-1.5 rounded bg-neutral-100 px-2 py-0.5 text-sm text-neutral-700"
                >
                  <CollectionColor
                    collection={collection}
                    className="shrink-0"
                  />
                  <SelectorTriggerLabel maxWidth={120} tooltipOnFocus={false}>
                    {collection.name}
                  </SelectorTriggerLabel>
                  <i
                    className="fa-solid fa-xmark h-3 w-3 shrink-0 cursor-pointer text-neutral-400 hover:text-neutral-600"
                    onClick={(e) => handleRemove(collection._id, e)}
                  />
                </span>
              ))
            ) : (
              <span className="text-neutral-500">Choose collections...</span>
            )}
          </div>
          <i
            aria-hidden
            className={cn(
              "fa-regular fa-chevron-down size-5 shrink-0 text-center text-[16px] leading-5 transition-transform",
              dropdownChevronClassName,
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={8}
        motion="rise"
        widthMode="trigger"
        style={triggerWidthStyle}
        className="rounded-lg border-0 p-0 shadow-none"
      >
        <Command
          filter={filterCollectionsByName}
          label="Collections"
          onKeyDown={navHandlers.onKeyDown}
          onPointerMove={navHandlers.onPointerMove}
          data-nav={navMode}
          className={collectionMenuClassName}
        >
          <CommandInput
            placeholder="Search collections..."
            wrapperClassName="h-10 shrink-0 px-3.5"
          />
          <CommandList className="min-h-0 max-h-none flex-1">
            <CommandEmpty>No collections found.</CommandEmpty>
            <CommandGroup className="overflow-visible p-0 pt-1">
              {props.collections.map((collection) => {
                const isSelected = props.selectedIds.includes(collection._id);
                return (
                  <CommandItem
                    key={collection._id}
                    value={`${collection.name} ${collection._id}`}
                    keywords={[collection.name]}
                    onSelect={() => handleToggle(collection._id)}
                    data-chosen={isSelected || undefined}
                    className={collectionMenuRowClassName}
                  >
                    <CollectionColor
                      collection={collection}
                      className="shrink-0"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {collection.name}
                    </span>
                    {isSelected ? <CollectionMenuTick /> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
